import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import type { AlertEvent } from '../types/alert';
import { getAllAlerts, closeAlert as closeAlertRequest } from '../lib/api/endpoints';
import { alertFromDto } from '../lib/api/mappers';
import { useAuth } from './AuthContext';
import { ApiError } from '../lib/api/client';

// Antes: hooks/useAlerts.ts se instanciaba de forma independiente en
// AppShell.tsx y AlertsPage.tsx, cada uno con su propia copia del arreglo
// (PENDIENTES.md/FE-02) — cerrar una alerta en una no actualizaba el
// contador de la otra. Mismo patrón que ChatContext, no una
// librería nueva de estado de servidor (esa decisión sigue abierta,
// Frontend_Rulebook.md §6).

interface AlertsContextValue {
  alerts: AlertEvent[];
  activeAlerts: AlertEvent[];
  criticalCount: number;
  isLoading: boolean;
  error: Error | null;
  /** Resuelve con el motivo legible si el cierre NO se pudo hacer, o `null` si salió bien. */
  closeAlert: (id: string, reason: string) => Promise<string | null>;
  upsertAlert: (alert: AlertEvent) => void;
}

const AlertsContext = createContext<AlertsContextValue | null>(null);

export function AlertsProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const [alerts, setAlerts] = useState<AlertEvent[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  // La carga se ata a QUIÉN está en sesión, no al montaje del provider
  // (issue #102). App.tsx ya remonta este provider al cambiar de identidad;
  // esta dependencia es lo que hace que el aislamiento no dependa de que ese
  // `key` siga ahí el día que alguien reordene los providers.
  const sessionKey = user ? `${user.tenantId}:${user.id}` : null;

  useEffect(() => {
    // Sin sesión no se pide nada, y el listado se vacía. Son dos cosas a la vez:
    // al ABRIR la app el provider ya existe pero el token todavía no, y ese
    // estado transitorio no puede congelarse en una lista vacía que sobreviva
    // al login; al SALIR, vaciar es lo que impide que la pantalla de login siga
    // enseñando las alertas de quien acaba de cerrar sesión. De paso se ahorra
    // un GET /alerts que solo podría volver 401.
    if (!sessionKey) {
      setAlerts([]);
      setError(null);
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    const load = async () => {
      setIsLoading(true);
      try {
        // FE-04: GET /alerts real en vez de mockData.ts. El listado es el
        // histórico REST — las críticas "en vivo" que hidratan por Firestore
        // son FE-05, sobre este mismo Context. `getAllAlerts()` recorre todas
        // las páginas (el contrato pagina de a 50/200) — un solo `getAlerts()`
        // truncaba en silencio en cuanto el tenant pasara de 50 alertas.
        const items = await getAllAlerts();
        if (cancelled) return;
        setAlerts(items.map(alertFromDto));
        setError(null);
      } catch (err) {
        if (!cancelled) {
          setAlerts([]);
          setError(err instanceof Error ? err : new Error('Error desconocido cargando alertas'));
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [sessionKey]);

  const activeAlerts = alerts.filter(a => a.status === 'active');
  const criticalCount = activeAlerts.filter(a => a.severity === 'critical').length;

  // FE-17: el POST /panic devuelve la alerta REAL —la que acaba de crear, o la
  // que ya estaba activa por idempotencia—, así que se inserta esa en vez de
  // refrescar el listado entero o fabricar un objeto en el cliente.
  //
  // Es un UPSERT por id, no un unshift: con el 200 idempotente la alerta que
  // vuelve puede llevar ya rato en la lista, y duplicarla haría creer al
  // conductor que declaró dos emergencias.
  //
  // Esto NO adelanta FE-05: sigue sin haber suscripción en vivo a las alertas
  // ajenas. Solo refleja el resultado de una acción propia.
  const upsertAlert = useCallback((alert: AlertEvent) => {
    setAlerts(prev => (prev.some(a => a.id === alert.id)
      ? prev.map(a => (a.id === alert.id ? alert : a))
      : [alert, ...prev]));
  }, []);

  const closeAlert = useCallback(async (id: string, reason: string): Promise<string | null> => {
    // Optimista: la UI refleja el cierre de inmediato: la confirmación viene
    // de un modal explícito, no de una acción accidental. Si el POST falla se
    // revierte Y se le dice a quien llama por qué (antes solo iba a la consola:
    // la alerta "se cerraba" y reaparecía activa sin una palabra).
    let previous: AlertEvent | undefined;
    setAlerts(prev => prev.map(a => {
      if (a.id !== id) return a;
      previous = a;
      return { ...a, status: 'closed', closedAt: new Date().toISOString(), closureReason: reason };
    }));

    try {
      const dto = await closeAlertRequest(id, reason);
      setAlerts(prev => prev.map(a => (a.id === id ? alertFromDto(dto) : a)));
      return null;
    } catch (err) {
      console.error('No se pudo cerrar la alerta en el backend', err);
      // 409: otra persona ya la había cerrado. La alerta SÍ está cerrada, así
      // que revertirla a "activa" mentiría; se conserva el estado y se quitan
      // los datos del cierre que puso este cliente (no fue este el cierre).
      const restored = previous;
      const alreadyClosed = err instanceof ApiError && err.status === 409;
      if (restored) {
        setAlerts(prev => prev.map(a => {
          if (a.id !== id) return a;
          return alreadyClosed
            ? { ...restored, status: 'closed', closedAt: undefined, closedBy: undefined, closureReason: undefined }
            : restored;
        }));
      }
      if (alreadyClosed) return 'Esa alerta ya estaba cerrada por otra persona.';
      if (err instanceof ApiError && err.status === 403) return 'Tu rol no tiene permiso para cerrar esta alerta.';
      return 'No se pudo cerrar la alerta. Inténtalo de nuevo.';
    }
  }, []);

  return (
    <AlertsContext.Provider value={{ alerts, activeAlerts, criticalCount, isLoading, error, closeAlert, upsertAlert }}>
      {children}
    </AlertsContext.Provider>
  );
}

export function useAlertsContext(): AlertsContextValue {
  const ctx = useContext(AlertsContext);
  if (!ctx) throw new Error('useAlertsContext must be used within AlertsProvider');
  return ctx;
}
